'use client';

import { cn, Slot, Text, useSlots } from '@magicschool/spellbook';
import NextImage from 'next/image';
import type { ReactNode } from 'react';

type IllustrationSlotProps = { src: string; width: number; height: number; priority?: boolean };

const StatusContainerIllustrationSlot = Slot<IllustrationSlotProps>({
  displayName: 'StatusContainer.Illustration',
  renderDefault: ({ src, width, height, priority }) => (
    <NextImage
      src={src}
      alt=""
      width={width}
      height={height}
      priority={priority}
      className="w-full h-auto mb-6"
      style={{ maxWidth: width }}
    />
  ),
});

const StatusContainerHeadingSlot = Slot<{ children?: ReactNode }>({
  displayName: 'StatusContainer.Heading',
  renderDefault: ({ children }) => (
    <Text variant="heading-1" element="h1">
      {children}
    </Text>
  ),
});

const StatusContainerBodySlot = Slot<{ children?: ReactNode }>({
  displayName: 'StatusContainer.Body',
  // element="div" so callers can pass their own block content (e.g. <Text> paragraphs) without
  // producing invalid p > p nesting, while bare strings still get body typography.
  renderDefault: ({ children }) => <Text element="div">{children}</Text>,
});

const StatusContainerActionsSlot = Slot<{ children?: ReactNode }>({
  displayName: 'StatusContainer.Actions',
  renderDefault: ({ children }) => <div className="flex gap-2 mt-2 empty:hidden">{children}</div>,
});

/**
 * Full-page status display for error, maintenance, and 404 pages. Vertically
 * and horizontally centers its content with consistent spacing.
 *
 * ### When to use
 * - HTTP error pages (404, 500, maintenance)
 * - `global-error.tsx` and `not-found.tsx` at the app root
 * - Any page that needs a centered illustration + heading + body + actions layout
 *
 * ### Composition (slots)
 * - `<StatusContainer.Illustration>`: Renders a `next/image`. Requires `src`,
 *   `width`, and `height`; `priority` is optional.
 * - `<StatusContainer.Heading>`: Renders children as an `<h1>` with `heading-1`
 *   typography.
 * - `<StatusContainer.Body>`: Renders children inside a `<div>` with body
 *   typography. Uses `element="div"` to safely accept block children such as
 *   multiple `<Text>` paragraphs without producing invalid `p > p` nesting.
 * - `<StatusContainer.Actions>`: Renders children in a horizontal button row.
 *   Hidden automatically when empty via `empty:hidden`.
 *
 * `className` merges onto the root: a surface with its own height, such as a
 * side panel, passes `min-h-0` to drop the 80dvh floor meant for a page.
 */
function StatusContainerInner({ children, className }: { children: ReactNode; className?: string }) {
  const { slots } = useSlots(children, {
    illustration: { Component: StatusContainerIllustrationSlot },
    heading: { Component: StatusContainerHeadingSlot },
    body: { Component: StatusContainerBodySlot },
    actions: { Component: StatusContainerActionsSlot },
  });

  return (
    <div className={cn('flex min-h-[80dvh] flex-col items-center justify-center gap-4 p-3 sm:p-6', className)}>
      {slots.illustration}
      <div className="text-center space-y-1 max-w-3xl empty:hidden">
        {slots.heading}
        {slots.body}
      </div>
      {slots.actions}
    </div>
  );
}

export const StatusContainer = Object.assign(StatusContainerInner, {
  Illustration: StatusContainerIllustrationSlot,
  Heading: StatusContainerHeadingSlot,
  Body: StatusContainerBodySlot,
  Actions: StatusContainerActionsSlot,
});
